// frontend/src/modules/reports/pages/financial/ExpensesPage.tsx
//
// Phase 4 (Reports Frontend), Task 4. Category x month matrix (committed —
// approved + reimbursed — cost per category per bucket) plus an
// approved-vs-pending split, both per currency.

import * as React from 'react';
import { ReportShell, type ReportRange } from '../../components/ReportShell';
import { StatCard } from '../../../../design-system/components/StatCard';
import { VISUAL } from '../../../../design-system/visual';
import { formatMoney } from '../../../../design-system/format';
import { reportsApi, downloadReportCsv } from '../../api';
import type { ExpensesReport, ExpensesRow } from '../../types';
import { useReportFetch } from './useReportFetch';
import { groupByCurrency } from './currencyGroup';
import { CheckCircle2, Clock3 } from 'lucide-react';

function buildMatrix(rows: ExpensesRow[]) {
  const categories = Array.from(new Set(rows.map((r) => r.category_name))).sort((a, b) => a.localeCompare(b));
  const buckets = Array.from(new Set(rows.map((r) => r.bucket))).sort((a, b) => a.localeCompare(b));
  const cell = new Map<string, number>();
  for (const r of rows) cell.set(`${r.category_name}::${r.bucket}`, r.committed_amount);
  return { categories, buckets, get: (cat: string, bucket: string) => cell.get(`${cat}::${bucket}`) ?? 0 };
}

export function ExpensesPage() {
  const [range, setRange] = React.useState<ReportRange | null>(null);

  const fetcher = range ? () => reportsApi.expenses({ from: range.from, to: range.to }) : null;
  const result = useReportFetch<ExpensesReport>(fetcher, [range?.from, range?.to]);
  const forbidden = result.status === 403;

  const report = result.data;
  const rows = report?.rows ?? [];
  const sections = groupByCurrency(rows);

  return (
    <ReportShell
      eyebrow="Reports / Financial"
      title="Expenses"
      description="Committed cost by category over time, and approved-vs-pending status."
      onRangeChange={setRange}
      access={!forbidden}
      accessDescription={forbidden ? (result.error ?? undefined) : undefined}
      loading={!forbidden && (range === null || result.loading)}
      error={forbidden ? null : result.error}
      onRetry={result.reload}
      isEmpty={!forbidden && range !== null && !result.loading && !result.error && rows.length === 0}
      csv={range ? {
        filename: `expenses-${range.from}-to-${range.to}.csv`,
        onDownload: () => downloadReportCsv(
          'expenses',
          { from: range.from, to: range.to },
          `expenses-${range.from}-to-${range.to}.csv`,
        ),
      } : null}
    >
      {report && (
        <div className="space-y-6">
          {sections.map(([currency, currencyRows]) => {
            const approved = currencyRows.reduce((s, r) => s + r.approved_amount + r.reimbursed_amount, 0);
            const pending = currencyRows.reduce((s, r) => s + r.submitted_amount, 0);
            const matrix = buildMatrix(currencyRows);
            return (
              <div key={currency} className="space-y-4">
                <h3 className="text-[13px] font-semibold text-muted-foreground uppercase tracking-[0.06em]">{currency}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <StatCard label="Approved + reimbursed" value={formatMoney(approved, currency)} icon={<CheckCircle2 />} />
                  <StatCard label="Pending approval" value={formatMoney(pending, currency)} icon={<Clock3 />} />
                </div>
                <div className={[VISUAL.bento, 'overflow-hidden overflow-x-auto'].join(' ')}>
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="bg-muted/50 border-b border-border/60">
                        <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Category</th>
                        {matrix.buckets.map((b) => (
                          <th key={b} className="px-4 py-3 text-right text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">{b}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {matrix.categories.map((cat) => (
                        <tr key={cat} className="border-b border-border/40 last:border-0">
                          <td className="px-4 py-2.5 font-medium text-foreground">{cat}</td>
                          {matrix.buckets.map((b) => (
                            <td key={b} className="px-4 py-2.5 text-right tabular-nums text-foreground">
                              {formatMoney(matrix.get(cat, b), currency)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </ReportShell>
  );
}
